import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import toast from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import type {
  BranchAddressPayload,
  BranchBasicInfoPayload,
  CreateBranchPayload,
  GetBranchesListParams,
  GetBranchesResponse,
  GetSingleBranchResponse,
  SetupFirstBranchesPayload,
  SetupFirstBranchesResponse,
} from '@/types/branches';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { mergeQueryOptions } from '@/utility/queryOptions';
import { fetchAllPages } from '@gokrew/ui/utility/fetchAllPages';
import { getApiErrorMessage } from '@gokrew/ui/utility/utility';
import {
  getBranches,
  getSingleBranch,
  setupFirstBranch,
  createNewBranch,
  toggleBranchStatus as toggleBranchStatusApi,
  deleteBranch as deleteBranchApi,
  updateBranchManagers,
  updateBranchAddressInfo,
  updateBranchBasicInfo,
  getLinkedBranches,
} from '@/api/branches';

export function useBranches(
  params: GetBranchesListParams,
  options: Omit<
    UseQueryOptions<GetBranchesResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId } = useAuth();

  return useQuery<GetBranchesResponse>({
    queryKey: [QueryKeys.Branches, params],
    queryFn: () => getBranches(companyId!, params),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export function useLinkedBranches(options?: { enabled?: boolean }) {
  const { companyId } = useAuth();

  // Not gated on `branches.read`: the endpoint only returns the caller's own
  // linked branches, which every admin needs for the branch switcher and forms.
  const isEnabled =
    Boolean(companyId) &&
    (options?.enabled !== undefined ? options.enabled : true);

  return useQuery({
    queryKey: [QueryKeys.LinkedBranches, companyId],
    queryFn: () => getLinkedBranches(companyId!),
    enabled: isEnabled,
    retry: false,
  });
}

export function useBranch(branchId: string) {
  const { companyId } = useAuth();

  return useQuery<GetSingleBranchResponse>({
    queryKey: [QueryKeys.Branches, branchId],
    queryFn: () => getSingleBranch(companyId!, branchId),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions({}, [!!companyId, !!branchId]),
  });
}

export function useCreateBranch(options?: {
  onSuccess?: (data: GetSingleBranchResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateBranchPayload) => {
      return createNewBranch(companyId!, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LinkedBranches] });
      toast.success(t('toast_message.created'));
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      toast.error(t('toast_message.create_failed'));
      options?.onError?.(error);
    },
  });
}

export function useUpdateBranchBasicInfo(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: BranchBasicInfoPayload;
    }) => updateBranchBasicInfo(companyId!, id, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      toast.success(t('toast_message.updated'));
      options?.onSuccess?.();
    },

    onError: (error) => {
      toast.error(t('toast_message.update_failed'));
      options?.onError?.(error);
    },
  });
}

export function useUpdateBranchAddress(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: BranchAddressPayload;
    }) => updateBranchAddressInfo(companyId!, id, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      toast.success(t('toast_message.updated'));
      options?.onSuccess?.();
    },

    onError: (error) => {
      toast.error(t('toast_message.update_failed'));
      options?.onError?.(error);
    },
  });
}

export function useUpdateBranchManagers(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { branchAdminIds: string[] };
    }) => updateBranchManagers(companyId!, id, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      toast.success(t('toast_message.updated'));
      options?.onSuccess?.();
    },
    onError: (error) => {
      toast.error(t('toast_message.update_failed'));
      options?.onError?.(error);
    },
  });
}

export function useToggleBranchStatus(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => toggleBranchStatusApi(companyId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      toast.success(t('toast_message.status_updated'));
      options?.onSuccess?.();
    },
    onError: (error) => {
      toast.error(t('toast_message.status_update_failed'));
      options?.onError?.(error);
    },
  });
}

export function useDeleteBranch(options?: { onSuccess?: () => void }) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteBranchApi(companyId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LinkedBranches] });
      toast.success(t('delete_location.deleted_toast'));
      options?.onSuccess?.();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('delete_location.failed_toast')));
    },
  });
}

export function useSetupFirstBranches(options?: {
  onSuccess?: (data: SetupFirstBranchesResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Branch);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SetupFirstBranchesPayload) =>
      setupFirstBranch(companyId!, payload),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Branches] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LinkedBranches] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Companies] });
      toast.success(t('toast_message.created'));
      options?.onSuccess?.(data);
    },

    onError: (error) => {
      toast.error(t('toast_message.create_failed'));
      options?.onError?.(error);
    },
  });
}

export const useInfiniteBranches = (
  params: Omit<GetBranchesListParams, 'page'>
) => {
  const { companyId } = useAuth();
  return useInfiniteQuery({
    queryKey: [QueryKeys.Branches, 'infinite', params],
    queryFn: ({ pageParam }) =>
      getBranches(companyId!, { ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.hasNextPage ? lastPage.data.meta.page + 1 : undefined,
    enabled: !!companyId,
  });
};

/**
 * Every location (all pages), for screens that list or name them all:
 * a company can have more than one page of them. Takes (and ignores) the
 * paging and search params `AsyncComboBox` sends, so it can back a picker
 * that then searches the whole list itself.
 */
export function useAllBranches(
  _pickerParams?: GetBranchesListParams & { name?: string },
  options: { enabled?: boolean } = {}
) {
  const { companyId } = useAuth();
  return useQuery<GetBranchesResponse>({
    queryKey: [QueryKeys.Branches, 'all'],
    queryFn: () =>
      fetchAllPages((page, limit) => getBranches(companyId!, { page, limit })),
    enabled: !!companyId && (options.enabled ?? true),
  });
}
